import { AlertCircle, AlertTriangle, Check, Info, X } from 'lucide-react'

import { Button } from '@/components/base'

export enum SnackbarType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
}

const TYPE_STYLES = {
  [SnackbarType.SUCCESS]: { icon: Check, color: 'bg-ok' },
  [SnackbarType.ERROR]: { icon: AlertCircle, color: 'bg-destructive' },
  [SnackbarType.INFO]: { icon: Info, color: 'bg-info' },
  [SnackbarType.WARNING]: { icon: AlertTriangle, color: 'bg-warn' },
} as const

export type SnackbarProps = {
  type: SnackbarType
  title: string
  action?: {
    label: string
    onClick: () => void
  }
  onClose?: () => void
  onHover?: (hovered: boolean) => void
  duration?: number
  isPaused?: boolean
}

export function Snackbar({
  type,
  title,
  action,
  onClose,
  onHover,
  duration,
  isPaused,
}: SnackbarProps) {
  const { icon: Icon, color } = TYPE_STYLES[type]
  const withProgress = duration !== undefined && duration > 0
  const pad = withProgress ? 'pb-5' : ''

  return (
    <div
      role="status"
      className="relative flex flex-row overflow-hidden rounded-md bg-inverse shadow-lg"
      onMouseEnter={onHover ? () => onHover(true) : undefined}
      onMouseLeave={onHover ? () => onHover(false) : undefined}
    >
      <div
        className={`flex items-center justify-center p-4 text-primary-foreground ${color} ${pad}`}
      >
        <Icon size={20} aria-hidden />
      </div>
      <div
        className={`flex flex-1 flex-row items-center justify-between gap-6 p-4 ${pad}`}
      >
        <span className="max-w-md text-sm text-inverse-foreground">
          {title}
        </span>
        <div className="flex flex-row items-center gap-2">
          {action ? (
            <Button
              size="sm"
              className="bg-inverse-foreground/15 text-inverse-foreground hover:bg-inverse-foreground/25"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ) : null}
          {onClose ? (
            <Button
              size="icon"
              variant="ghost"
              onClick={onClose}
              className="text-inverse-foreground hover:bg-inverse-foreground/15"
            >
              <X size={15} aria-hidden />
            </Button>
          ) : null}
        </div>
      </div>
      {withProgress ? (
        <div className="absolute bottom-0 left-0 h-2 w-full bg-inverse-foreground/15">
          <div
            className="h-full w-full rounded-r-md bg-inverse-foreground/90"
            style={{
              animation: `snackbar-shrink ${duration}ms linear forwards`,
              animationPlayState: isPaused ? 'paused' : 'running',
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
